import React, { memo } from "react"

import { useReviewSubjectAnnotation } from "@/human-feedback/annotations"
import {
  yesNoBadgeColorClasses,
  yesNoBadgeEmptyClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { isAutomaticExpectedBehaviorEnabled } from "../../configuration/utils/isExpectedBehaviorCompositeMetric"
import {
  getCompositeResultData,
  getCompositeReviewColumnClasses,
} from "./composite-evaluation-types"
import { getCompositeJudgeScore } from "./composite-evaluation-values"

interface CompositeJudgeScoresProps {
  annotationsForMetric: Annotation[]
  metricOutput?: RunMetricOutput
  metadataCriteria?: string[] | null
  reportingMethod: string
  /** Blind review hides the machine total so the reviewer isn't anchored. */
  showAiScore?: boolean
  labelScores?: boolean
}

/** A judge's total, or a placeholder while that judge has yet to rule on every
 *  criterion, so the chip keeps its footprint either way. The column beneath
 *  names the judge, so the chip carries no label of its own. */
const JudgeScore: React.FC<{
  score: { label: string; passed: boolean | null } | null
  judge: string
}> = ({ score, judge }) => {
  const color =
    score == null
      ? yesNoBadgeEmptyClasses
      : score.passed == null
        ? yesNoBadgeColorClasses.default
        : score.passed
          ? yesNoBadgeColorClasses.success
          : yesNoBadgeColorClasses.danger
  return (
    <span
      className={`${yesNoBadgeShapeClasses} ${color}`}
      aria-label={
        score == null ? `No ${judge} score yet` : `${judge}: ${score.label}`
      }
    >
      {score?.label ?? "-"}
    </span>
  )
}

/**
 * Both judges' totals for one composite metric, from the AI verdicts and
 * reviewer votes. Labels identify totals above behavior cards; legacy tables
 * align each total with the column it summarizes.
 */
export const CompositeJudgeScores: React.FC<CompositeJudgeScoresProps> = memo(
  ({
    annotationsForMetric,
    metricOutput,
    metadataCriteria,
    reportingMethod,
    showAiScore = true,
    labelScores = false,
  }) => {
    const myAnnotation = useReviewSubjectAnnotation(annotationsForMetric)

    const resultJson = getCompositeResultData(metricOutput)
    const aiValues = resultJson?.individual_results?.map(
      (result) => result.result,
    )
    const criteriaCount = aiValues?.length ?? metadataCriteria?.length ?? 0

    if (criteriaCount === 0) {
      return null
    }

    const columns = getCompositeReviewColumnClasses()
    return (
      <div
        className={
          labelScores
            ? "flex w-full flex-wrap justify-end gap-3"
            : "flex w-full justify-end"
        }
      >
        {showAiScore && (
          <div
            className={
              labelScores ? "flex items-center gap-2" : `${columns.ai} px-3`
            }
          >
            {labelScores && (
              <span className="text-xs text-coval-text-muted">AI Judge</span>
            )}
            <JudgeScore
              judge={isAutomaticExpectedBehaviorEnabled() ? "AI Judge" : "AI"}
              score={getCompositeJudgeScore(
                aiValues,
                reportingMethod,
                criteriaCount,
              )}
            />
          </div>
        )}
        <div
          className={
            labelScores ? "flex items-center gap-2" : `${columns.human} px-3`
          }
        >
          {labelScores && (
            <span className="text-xs text-coval-text-muted">Human</span>
          )}
          <JudgeScore
            judge="Human"
            score={getCompositeJudgeScore(
              myAnnotation?.ground_truth_set_value,
              reportingMethod,
              criteriaCount,
            )}
          />
        </div>
      </div>
    )
  },
)
CompositeJudgeScores.displayName = "CompositeJudgeScores"
